/** Resolve bundled tier seat products from WooCommerce REST (SUSEE-1314 / SUSEE-1546). */

export type BillingPeriodSlug = 'monthly' | 'quarterly' | 'yearly';

export type WcSeatProduct = {
  id: number;
  name: string;
  sku?: string;
  price: string | number;
  usd_price?: string | number;
  _ss_seat_min?: number | null;
  _ss_seat_max?: number | null;
  _ss_plan_code?: string | null;
  _ss_billing_period?: string | null;
  _ss_ap_public?: string | null;
  ss_per_user_price?: {
    inr?: number;
    usd?: number;
    days_remaining?: number;
  };
};

export function resolveWcCurrencyScope(billingCountry: string | undefined | null): 'inr' | 'usd' {
  return billingCountry === 'IN' ? 'inr' : 'usd';
}

export function billingPeriodLabelToSlug(
  planType: string | undefined | null
): BillingPeriodSlug {
  const p = (planType || 'Monthly').toString().toLowerCase();
  if (p.includes('quarter') || p.includes('3 month')) {
    return 'quarterly';
  }
  if (p.includes('year') || p.includes('annual')) {
    return 'yearly';
  }
  return 'monthly';
}

function productPlanCode(product: WcSeatProduct): string {
  return (product._ss_plan_code || '').toString().toLowerCase().trim();
}

function productPeriod(product: WcSeatProduct): string {
  return (product._ss_billing_period || '').toString().toLowerCase().trim();
}

function productMatchesSeatBand(product: WcSeatProduct, seats: number): boolean {
  const minRaw = product._ss_seat_min;
  if (minRaw === null || minRaw === undefined) {
    return false;
  }
  const min = Number(minRaw);
  const max = Number(product._ss_seat_max ?? 0);
  if (!Number.isFinite(min)) {
    return false;
  }
  if (seats < min) {
    return false;
  }
  if (max > 0 && seats > max) {
    return false;
  }
  return true;
}

function bandSku(planCode: string, period: BillingPeriodSlug, seats: number): string | null {
  if (period === 'monthly') {
    return null;
  }
  const code = (planCode || 'professional').toLowerCase().trim();
  if (seats <= 4) {
    return period === 'yearly' ? `ss-${code}-yearly-1-4` : null;
  }
  if (seats <= 29) {
    return `ss-${code}-${period}-5-29`;
  }
  return `ss-${code}-${period}-30plus`;
}

/**
 * Find seat product for tier + billing period (+ seats for band SKUs).
 */
export function findBundledSeatProduct(
  products: WcSeatProduct[] | null | undefined,
  planCode: string,
  period: BillingPeriodSlug,
  currency: 'inr' | 'usd',
  seats: number = 5
): WcSeatProduct | null {
  if (!products?.length) {
    return null;
  }

  const code = (planCode || 'professional').toLowerCase().trim();
  const seatCount = Math.max(1, Math.round(Number(seats) || 1));

  if (period === 'quarterly' || period === 'yearly') {
    const exactSku = bandSku(code, period, seatCount);
    if (exactSku) {
      const byBandSku = products.find((p) => (p.sku || '').toLowerCase() === exactSku);
      if (byBandSku) {
        return byBandSku;
      }
    }

    const byBandMeta = products.find((p) => {
      const pCode = productPlanCode(p) || inferPlanCodeFromSkuOrName(p);
      const pPeriod = productPeriod(p);
      if (pCode !== code || pPeriod !== period) {
        return false;
      }
      return productMatchesSeatBand(p, seatCount);
    });
    if (byBandMeta) {
      return byBandMeta;
    }

    // Legacy flat products (pre-band): Professional quarterly SKU is ss-professional-quarter.
    if (period === 'quarterly' && code === 'professional') {
      const legacyQuarter = products.find(
        (p) => (p.sku || '').toLowerCase() === 'ss-professional-quarter'
      );
      if (legacyQuarter) {
        return legacyQuarter;
      }
    }
  }

  const singleCurrencySku = `ss-${code}-${period}`;
  const bySingleCurrencySku = products.find((p) => (p.sku || '').toLowerCase() === singleCurrencySku);
  if (bySingleCurrencySku) {
    return bySingleCurrencySku;
  }

  const sku = `ss-${code}-${period}-${currency}`;
  const bySku = products.find((p) => (p.sku || '').toLowerCase() === sku);
  if (bySku) {
    return bySku;
  }

  if (code === 'professional' && currency === 'inr' && period !== 'quarterly') {
    const legacySku =
      period === 'yearly' ? 'supersee-yearly-local' : 'supersee-monthly-local';
    const byLegacySku = products.find((p) => (p.sku || '').toLowerCase() === legacySku);
    if (byLegacySku) {
      return byLegacySku;
    }
    const legacyName = period === 'yearly' ? 'Yearly' : 'Monthly';
    const byName = products.find((p) => p.name === legacyName);
    if (byName) {
      return byName;
    }
  }

  return findBundledSeatProductByTitle(products, code, period, currency);
}

function inferPlanCodeFromSkuOrName(product: WcSeatProduct): string {
  const hay = `${product.sku || ''} ${product.name || ''}`.toLowerCase();
  if (hay.includes('business')) {
    return 'business';
  }
  if (hay.includes('basic')) {
    return 'basic';
  }
  return 'professional';
}

const TIER_TITLE_FRAGMENTS: Record<string, string> = {
  basic: 'basic',
  professional: 'professional',
  business: 'business',
};

/** Fallback when REST omits sku — matches seeded product titles. */
export function findBundledSeatProductByTitle(
  products: WcSeatProduct[] | null | undefined,
  planCode: string,
  period: BillingPeriodSlug,
  _currency: 'inr' | 'usd'
): WcSeatProduct | null {
  if (!products?.length) {
    return null;
  }
  const tier = TIER_TITLE_FRAGMENTS[(planCode || 'professional').toLowerCase().trim()];
  const periodNeedle =
    period === 'yearly'
      ? ['year', '12 month']
      : period === 'quarterly'
        ? ['3 month', 'quarter']
        : ['1 month', 'monthly'];
  return (
    products.find((p) => {
      const name = (p.name || '').toLowerCase();
      if (tier && !name.includes(tier) && p.name !== 'Monthly' && p.name !== 'Yearly') {
        return false;
      }
      return periodNeedle.some((needle) => name.includes(needle));
    }) || null
  );
}

export function readProductInrPrice(product: WcSeatProduct): number {
  return Number(product.price) || 0;
}

export function readProductUsdPrice(
  product: WcSeatProduct,
  billingCountry: string | undefined | null
): number {
  const usd = Number(product.usd_price) || 0;
  const inr = readProductInrPrice(product);
  if (billingCountry !== 'IN' && usd === 0 && inr !== 0) {
    return inr;
  }
  return usd;
}

/** Effective monthly rate for display (prepaid ÷ months). */
export function effectiveMonthlyFromPrepaid(
  prepaid: number,
  period: BillingPeriodSlug
): number {
  if (period === 'quarterly') {
    return prepaid / 3;
  }
  if (period === 'yearly') {
    return prepaid / 12;
  }
  return prepaid;
}

/** @deprecated Prefer periodOptions().savingsPercent — kept for transitional call sites. */
export function saveChipForBand(seats: number, period: BillingPeriodSlug): string {
  if (seats <= 4) {
    return period === 'yearly' ? 'Annual only' : '';
  }
  if (period === 'quarterly') {
    return seats >= 30 ? '~17% off' : '12.5% off';
  }
  if (period === 'yearly') {
    return seats >= 30 ? 'Save ~28%' : 'Save 25%';
  }
  return '';
}

export type PeriodOption = {
  period: BillingPeriodSlug;
  productId: number | null;
  available: boolean;
  lockReason: string | null;
  perSeatTerm: number;
  perSeatMonthly: number;
  termMonths: 1 | 3 | 12;
  totalTerm: number;
  savingsVsMonthlyPerYear: number;
  savingsPercent: number;
  badge: 'best_value' | null;
};

function termMonthsFor(period: BillingPeriodSlug): 1 | 3 | 12 {
  if (period === 'quarterly') {
    return 3;
  }
  if (period === 'yearly') {
    return 12;
  }
  return 1;
}

function readSeatPrice(
  product: WcSeatProduct | null,
  currency: 'inr' | 'usd',
  billingCountry: string | null
): number {
  if (!product) {
    return 0;
  }
  return currency === 'usd'
    ? readProductUsdPrice(product, billingCountry)
    : readProductInrPrice(product);
}

/**
 * Build Monthly / Quarterly / Annual options with real savings vs Monthly.
 */
export function periodOptions(
  products: WcSeatProduct[],
  planCode: string,
  seats: number,
  currency: 'inr' | 'usd',
  billingCountry: string | null
): PeriodOption[] {
  const seatCount = Math.max(1, Math.round(Number(seats) || 1));
  const micro = seatCount <= 4;
  const periods: BillingPeriodSlug[] = ['monthly', 'quarterly', 'yearly'];

  const monthlyProduct = findBundledSeatProduct(
    products,
    planCode,
    'monthly',
    currency,
    Math.max(seatCount, 5)
  );
  const monthlyPerSeat = readSeatPrice(monthlyProduct, currency, billingCountry);

  const options: PeriodOption[] = periods.map((period) => {
    const termMonths = termMonthsFor(period);
    let available = true;
    let lockReason: string | null = null;
    if (micro && period !== 'yearly') {
      available = false;
      lockReason = 'Teams of 1 to 4 seats are billed annually.';
    }

    const product = available
      ? findBundledSeatProduct(products, planCode, period, currency, seatCount)
      : null;
    const perSeatTerm = readSeatPrice(product, currency, billingCountry);
    const perSeatMonthly = effectiveMonthlyFromPrepaid(perSeatTerm, period);
    const totalTerm = Math.round(perSeatTerm * seatCount * 100) / 100;

    let savingsVsMonthlyPerYear = 0;
    let savingsPercent = 0;
    if (available && monthlyPerSeat > 0 && perSeatMonthly > 0 && period !== 'monthly') {
      savingsVsMonthlyPerYear =
        Math.round((monthlyPerSeat * 12 * seatCount - perSeatMonthly * 12 * seatCount) * 100) /
        100;
      if (savingsVsMonthlyPerYear < 0) {
        savingsVsMonthlyPerYear = 0;
      }
      savingsPercent = Math.round(
        ((monthlyPerSeat - perSeatMonthly) / monthlyPerSeat) * 100
      );
      if (savingsPercent < 0) {
        savingsPercent = 0;
      }
    }

    return {
      period,
      productId: product?.id ?? null,
      available,
      lockReason,
      perSeatTerm,
      perSeatMonthly: Math.round(perSeatMonthly * 100) / 100,
      termMonths,
      totalTerm,
      savingsVsMonthlyPerYear,
      savingsPercent,
      badge: null,
    };
  });

  const availableOpts = options.filter((o) => o.available && o.perSeatMonthly > 0);
  if (availableOpts.length) {
    const best = availableOpts.reduce((a, b) =>
      a.perSeatMonthly <= b.perSeatMonthly ? a : b
    );
    const match = options.find((o) => o.period === best.period);
    if (match && match.period !== 'monthly') {
      match.badge = 'best_value';
    }
  }

  return options;
}

/**
 * Hint when within 5 seats of the next cheaper band (prepaid periods only).
 */
export function nextBandHint(
  products: WcSeatProduct[],
  planCode: string,
  period: BillingPeriodSlug,
  seats: number,
  currency: 'inr' | 'usd',
  billingCountry: string | null
): { seatsNeeded: number; perSeatMonthly: number } | null {
  if (period === 'monthly') {
    return null;
  }
  const seatCount = Math.max(1, Math.round(Number(seats) || 1));
  const thresholds = [5, 30];
  const next = thresholds.find((t) => seatCount < t && t - seatCount <= 5);
  if (!next) {
    return null;
  }
  const seatsNeeded = next - seatCount;
  const nextProduct = findBundledSeatProduct(
    products,
    planCode,
    period,
    currency,
    next
  );
  if (!nextProduct) {
    return null;
  }
  const perSeatTerm = readSeatPrice(nextProduct, currency, billingCountry);
  if (perSeatTerm <= 0) {
    return null;
  }
  return {
    seatsNeeded,
    perSeatMonthly:
      Math.round(effectiveMonthlyFromPrepaid(perSeatTerm, period) * 100) / 100,
  };
}

export function periodDisplayName(period: BillingPeriodSlug): string {
  if (period === 'quarterly') {
    return 'Quarterly';
  }
  if (period === 'yearly') {
    return 'Annual';
  }
  return 'Monthly';
}

export function periodTermLabel(period: BillingPeriodSlug): string {
  if (period === 'quarterly') {
    return '3 months';
  }
  if (period === 'yearly') {
    return 'year';
  }
  return '1 month';
}
